import React from "react";
import Button from "./Button";

/**
 * SectionHeader
 * A reusable, token-driven page/section heading with optional divider and subtitle.
 *
 * Props:
 * - title (string | ReactNode): Main heading text. (required)
 * - subtitle (string | ReactNode): Optional supporting subtitle.
 * - variant ("h1"|"h2"|"h3"|"h4"|"h5"|"h6"): Semantic heading level. Default "h2".
 * - align ("left"|"center"): Horizontal alignment. Default "left".
 * - showDivider (boolean): Show the accent divider below the title. Default true.
 * - uppercase (boolean): Uppercase title. Default true (matches design).
 * - className (string): Extra classes for the root element.
 * - actionButton (ReactNode | ButtonProps): An action button to render.
 */
const SectionHeader = ({
  title = "Featured Work",
  eyebrow,
  subtitle,
  variant = "h2",
  pageTitle = false,
  align = "left",
  showDivider = true,
  uppercase = true,
  className = "",
  actionButton = null,
  subtitleClass = "",
  ...rest
}) => {
  const allowed = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);
  let desired = (variant || "h2").toLowerCase();
  if (pageTitle) desired = "h1";
  const tag = allowed.has(desired) ? desired : "h2";
  const Heading = tag;
  // Derive subtitle heading tag as one level smaller (e.g., h2 -> h3)
  const currentLevel = Number(tag.replace("h", "")) || 2;
  const subtitleLevel = Math.min(currentLevel + 1, 6);
  const SubtitleHeading = `h${subtitleLevel}`;

  const renderAction = () => {
    if (!actionButton) return null;
    if (React.isValidElement(actionButton)) return actionButton;
    // assume actionButton is props for Button component
    return <Button {...actionButton} />;
  };

  return (
    <header
      className={[
        "rm-section-header",
        `rm-section-header--${tag}`,
        pageTitle ? "pageHeader" : "",
        `rm-section-header--align-${align}`,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      <div className="rm-section-header__inner">
        <div className="rm-section-header__content">
          {eyebrow && (
            <p className="rm-section-header__eyebrow">{eyebrow}</p>
          )}
          <Heading
            className={[
              "rm-section-header__title",
              pageTitle ? "pageHeader" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {title}
          </Heading>
          {showDivider && (
            <div className="rm-section-header__divider" aria-hidden="true">
              <span className="rm-section-header__divider-line" />
            </div>
          )}
          {subtitle && (
            <span className={[subtitleClass].filter(Boolean).join(" ")}>
              {subtitle}
            </span>
          )}
        </div>

        {actionButton && (
          <div
            className="rm-section-header__actions"
            aria-hidden={!actionButton}
          >
            {renderAction()}
          </div>
        )}
      </div>
    </header>
  );
};

export default SectionHeader;
